/**
 * Этап 4 / KS-07 — the ONE definition of what "rich text" means in this system.
 *
 * The system's text is moving from Markdown to HTML everywhere (documents, emails,
 * questionnaire), because Markdown cannot express colour or an image in a
 * signature — see ADR 2026-07-29 «KS-07». Three things must agree on the tag set,
 * or the transition leaks: the editor (what a human can produce), the sanitizer
 * (what is allowed to reach the DB and outbound mail), and the `.docx` bridge
 * (what survives an export). They agree by importing THIS module.
 *
 * Deliberately dependency-free and DOM-free: `@eer/shared` is imported by the
 * browser bundle, so nothing here may touch `document`, `Buffer` or a Node API.
 * The actual sanitizing runs server-side (`api/services/htmlSanitize.ts`) where a
 * real parser is available — an allow-list is data, a parser is not, and
 * hand-rolling the parser for hostile input is how sanitizers get bypassed.
 *
 * ── Why a normalizer exists alongside the rewritten prompts.
 * The ~11 `ai_prompts` rows are being rewritten to emit HTML. Models do not obey
 * output-format instructions perfectly, and the fallback matters: a stray
 * Markdown answer must not reach a client as literal `**bold**`. So every ingest
 * passes through {@link normalizeRichText}, which converts Markdown to HTML and
 * leaves real HTML alone. That also means the transition does not depend on all 11
 * prompts being retuned first (the ordering ADR KS-07 asked for but the blocked
 * Anthropic key cannot deliver yet).
 */
/**
 * Block + inline tags a rich-text value may contain.
 *
 * Kept intentionally small: every tag here must be renderable on screen, in an
 * email body, AND in the `.docx` bridge (`api/services/htmlToDocx.ts`). Adding a
 * tag without teaching that bridge about it degrades the exported document to
 * plain text — the bridge's docstring says so explicitly.
 */
export const RICH_TEXT_ALLOWED_TAGS = [
    'p',
    'br',
    'h1',
    'h2',
    'h3',
    'ul',
    'ol',
    'li',
    'b',
    'strong',
    'i',
    'em',
    'u',
    's',
    'a',
    'span',
    'blockquote',
    'pre',
    'code',
    'hr',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'img',
];
/**
 * Attributes allowed per tag. `style` is allowed ONLY where the editor can emit
 * it, and its contents are filtered separately by
 * {@link RICH_TEXT_ALLOWED_STYLE_PROPS} — an unfiltered `style` is an injection
 * surface of its own (`expression()`, `url(javascript:…)`, absolute positioning
 * over other content).
 */
export const RICH_TEXT_ALLOWED_ATTRS = {
    a: ['href', 'title', 'target', 'rel'],
    span: ['style'],
    p: ['style'],
    h1: ['style'],
    h2: ['style'],
    h3: ['style'],
    li: ['style'],
    td: ['style', 'colspan', 'rowspan'],
    th: ['style', 'colspan', 'rowspan'],
    table: ['style'],
    // `cid:` sources are how a signature image travels inside an email (ADR
    // 2026-07-29 «KS-06 ч.2»): width/height keep a logo from rendering full-bleed.
    img: ['src', 'alt', 'width', 'height', 'style'],
};
/**
 * URL schemes a link or image may use.
 *
 * `http(s)` and `mailto` carry over from the markdown link allow-list (PK-QA-23) —
 * that restriction was a security fix and must not regress. `cid` is added for
 * signature images embedded in outgoing mail. `data:` is deliberately ABSENT:
 * Gmail and Outlook strip `data:` images from a body anyway, so allowing it would
 * buy an injection surface in exchange for nothing.
 */
export const RICH_TEXT_ALLOWED_SCHEMES = ['http', 'https', 'mailto', 'cid'];
/**
 * CSS properties that survive sanitizing, with the value patterns they accept.
 *
 * Colours are restricted to hex / `rgb()` / a bare colour keyword. A pattern
 * rather than a free string is the point: it is what stops `url(...)`,
 * `expression(...)` and escape-sequence tricks from riding in on a colour.
 */
export const RICH_TEXT_ALLOWED_STYLE_PROPS = {
    color: [
        /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i,
        /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i,
        /^[a-z]+$/i,
    ],
    'background-color': [
        /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i,
        /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i,
        /^[a-z]+$/i,
    ],
    'text-align': [/^(?:left|right|center|justify)$/i],
    'font-weight': [/^(?:bold|normal|[1-9]00)$/i],
    'text-decoration': [/^(?:underline|line-through|none)$/i],
};
/**
 * True when the value already carries rich-text HTML.
 *
 * The test is a KNOWN TAG from the allow-list, not "contains a `<`". Prose about
 * salary ("< 100k") or a generated comparison ("5 < 10") contains angle brackets
 * and is NOT HTML; treating it as HTML would send it to the sanitizer, which would
 * silently drop the `< 100k` as a broken tag. Getting this backwards loses words
 * out of a client-facing document, so the check errs toward "this is Markdown".
 */
export function looksLikeHtml(value) {
    const text = String(value ?? '');
    if (text.length === 0)
        return false;
    const tags = RICH_TEXT_ALLOWED_TAGS.join('|');
    return new RegExp(`<(?:${tags})(?:\\s[^<>]*)?/?>`, 'i').test(text);
}
/**
 * Coerce any stored/generated text into rich-text HTML.
 *
 * Real HTML passes through untouched (the caller sanitizes it — this module has no
 * parser). Anything else is treated as Markdown and rendered by the shared
 * `renderFreeMarkdown`, which escapes every segment, so untrusted model output
 * cannot inject markup by taking this branch.
 *
 * NOT a sanitizer, and must never be mistaken for one: the HTML branch returns its
 * input verbatim. Callers that persist or send the result MUST sanitize after.
 */
export function normalizeRichText(value, renderMarkdown) {
    const text = stripWrappingCodeFence(value);
    if (text.trim().length === 0)
        return '';
    return looksLikeHtml(text) ? text : renderMarkdown(text);
}
/** A ```lang fence that wraps the WHOLE value (opening + closing). */
const WRAPPING_FENCE = /^\s*```[a-z0-9+#-]*[ \t]*\r?\n?([\s\S]*?)\r?\n?[ \t]*```\s*$/i;
/** Only the opener — a truncated answer never emits the closing fence. */
const LEADING_FENCE = /^\s*```[a-z0-9+#-]*[ \t]*\r?\n?/i;
/**
 * Remove a markdown code fence that WRAPS the whole value.
 *
 * Живое доказательство, зачем это нужно (dev, 2026-07-30): после перевода промтов
 * на HTML модель вернула тело клиентского follow-up письма как
 * `` ```html\n<p>Dear …</p>\n``` `` — HTML внутри markdown-ограждения, хотя промт
 * прямо запрещает fences. `looksLikeHtml` находит `<p>` и отдаёт значение как
 * готовый HTML, поэтому обратные кавычки доезжают до получателя ВИДИМЫМ текстом в
 * первой строке письма. Ограждение снимается здесь, в единственной точке
 * нормализации, поэтому чинятся сразу все поверхности: ingest на сервере, egress
 * перед отправкой, редактор и режим чтения.
 *
 * Снимается ТОЛЬКО обрамляющее ограждение: тройные кавычки в середине текста —
 * это содержимое документа, а не разметка ответа модели, и трогать их нельзя.
 */
export function stripWrappingCodeFence(value) {
    const text = String(value ?? '');
    const wrapped = WRAPPING_FENCE.exec(text);
    if (wrapped)
        return wrapped[1] ?? '';
    // Незакрытое ограждение: убираем открывашку, содержимое сохраняем целиком.
    if (LEADING_FENCE.test(text) && !text.trimEnd().endsWith('```')) {
        return text.replace(LEADING_FENCE, '');
    }
    return text;
}
/**
 * The readable text of a rich-text value, with markup removed.
 *
 * For previews, truncations and counters — anywhere a value must fit on one line or
 * be measured in characters. `truncate(html, 120)` without this counts `<p>` toward
 * the budget and can cut a tag in half, and the user sees `<stro`.
 *
 * NOT a sanitizer and not a substitute for one: it is a display helper for contexts
 * where no markup is wanted at all. Tag removal is deliberately crude (this module
 * has no parser) — safe here precisely because the RESULT is inserted as text.
 */
export function richTextToPlainText(value) {
    return (String(value ?? '')
        // A block boundary is a space, else "…engineer.</p><p>Remote…" welds two words.
        .replace(/<\/(?:p|div|h[1-6]|li|tr|blockquote)>/gi, ' ')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/g, "'")
        .replace(/[ \t]+/g, ' ')
        .trim());
}
/**
 * True when the value is EMPTY as rich text — no text and no image.
 *
 * `<p></p>` is what an emptied editor leaves behind, and a naive
 * `body.trim().length > 0` check reads it as content. That is how a "send" button
 * stays enabled over a blank email. An `<img>` counts as content even with no
 * text, because a signature can legitimately be just a logo.
 */
export function isRichTextEmpty(value) {
    const html = String(value ?? '');
    if (/<img\b/i.test(html))
        return false;
    return (html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim().length === 0);
}
/**
 * E03-F08-T06 — characters that, standing alone, do not make a list item content:
 * whitespace, non-breaking / zero-width spaces, and the bullet glyphs a model types
 * by hand inside an `<li>` (`<li>•</li>` renders as a marker next to a marker).
 */
const BLANK_ITEM_CHARS = /[\s\u00a0\u200b-\u200d\u2060\ufeff•·▪◦‣∙●○■□*\-–—]/g;
/**
 * True when a list item (its inner HTML, or a plain/Markdown string) carries no
 * readable content — nothing but whitespace, `&nbsp;`, `<br>`, empty tags or a lone
 * bullet glyph. An `<img>` counts as content, as in {@link isRichTextEmpty}.
 *
 * Why it exists (E03-F08-T06): the model sometimes emits `<li></li>` / `<li><br></li>`
 * / `<li>&nbsp;</li>`, and every surface faithfully turned it into a bullet with no
 * text — on screen before «MUST Requirements», and twice in the exported Kickoff
 * Sheet. A client-facing document must not show those.
 */
export function isBlankListItem(value) {
    const html = String(value ?? '');
    if (/<img\b/i.test(html))
        return false;
    return (html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|&#160;|&#x0*a0;|&#8203;|&#x200b;|&zwnj;|&zwj;/gi, ' ')
        .replace(BLANK_ITEM_CHARS, '').length === 0);
}
/** A leaf `<li>` — its content holds no nested list markup, so the match is exact. */
const LEAF_LIST_ITEM = /<li\b[^>]*>((?:(?!<\/?(?:li|ul|ol)\b)[\s\S])*?)<\/li\s*>/gi;
/** A list left with no items once its blank ones are gone. */
const EMPTY_LIST = /<(ul|ol)\b[^>]*>\s*<\/\1\s*>/gi;
/**
 * Remove blank list items (see {@link isBlankListItem}) from rich-text HTML, and any
 * `<ul>`/`<ol>` that ends up with no items. Everything else is returned verbatim.
 *
 * Only LEAF items are examined (no nested `<ul>/<ol>/<li>` inside): a lazy regex
 * over a nested list would pair an outer `<li>` with an inner `</li>` and cut the
 * structure apart. Repeats until stable so a list emptied by the pass, inside an
 * item that held only that list, is cleaned too.
 *
 * NOT a sanitizer — run it on already-sanitized HTML (or before the sanitizer; it
 * only ever deletes markup).
 */
export function dropEmptyListItems(value) {
    let html = String(value ?? '');
    if (!/<li\b/i.test(html))
        return html;
    for (let i = 0; i < 5; i += 1) {
        const next = html
            .replace(LEAF_LIST_ITEM, (whole, inner) => (isBlankListItem(inner) ? '' : whole))
            .replace(EMPTY_LIST, '');
        if (next === html)
            break;
        html = next;
    }
    return html;
}
//# sourceMappingURL=richText.js.map